import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import {
  parseFolderSelection,
  resolveByBarcodes,
  runBulkAssign,
  ParsedFolder,
  BarcodeResolveResult,
  AssignProgress,
  AssignRunResult,
} from './services/bulkProductImageAssign';

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  createdBy?: string;
  /** Yükleme tamamlanınca (kütüphaneyi tazelemek için) */
  onCompleted?: () => void;
  maxImagesPerProduct?: number;
}

type WizardStep = 0 | 1 | 2;

const BulkProductImageAssignWizard: React.FC<Props> = ({
  open,
  onClose,
  companyId,
  createdBy,
  onCompleted,
  maxImagesPerProduct = 8,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<WizardStep>(0);
  const [error, setError] = useState<string | null>(null);

  const [folders, setFolders] = useState<ParsedFolder[]>([]);
  const [resolving, setResolving] = useState(false);
  const [resolveMap, setResolveMap] = useState<Map<string, BarcodeResolveResult>>(new Map());
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<AssignProgress | null>(null);
  const [result, setResult] = useState<AssignRunResult | null>(null);

  // Önizleme için object URL'leri (her klasörün ilk resmi)
  const previewUrlsRef = useRef<string[]>([]);
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());

  const revokePreviews = useCallback(() => {
    previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrlsRef.current = [];
  }, []);

  const resetState = useCallback(() => {
    setStep(0);
    setError(null);
    setFolders([]);
    setResolving(false);
    setResolveMap(new Map());
    setSelected(new Set());
    setRunning(false);
    setProgress(null);
    setResult(null);
    revokePreviews();
    setPreviews(new Map());
  }, [revokePreviews]);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    return () => {
      revokePreviews();
    };
  }, [revokePreviews]);

  // Klasörler değişince ilk resimlerin önizleme URL'lerini üret
  useEffect(() => {
    revokePreviews();
    const map = new Map<string, string>();
    folders.forEach((f) => {
      const first = f.files[0]?.file;
      if (first) {
        const url = URL.createObjectURL(first);
        previewUrlsRef.current.push(url);
        map.set(f.barcode, url);
      }
    });
    setPreviews(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  const totalImages = useMemo(
    () => folders.reduce((sum, f) => sum + f.files.length, 0),
    [folders]
  );

  // ── Step 0: klasör seçimi ──
  const handlePickClick = () => fileInputRef.current?.click();

  /** Yeni seçimi mevcut listeyle birleştir (aynı barkod varsa üzerine yaz). */
  const mergeFolders = (incoming: ParsedFolder[], existing: ParsedFolder[]): ParsedFolder[] => {
    const map = new Map<string, ParsedFolder>();
    existing.forEach((f) => map.set(f.barcode.toLowerCase(), f));
    incoming.forEach((f) => map.set(f.barcode.toLowerCase(), f));
    return Array.from(map.values()).sort((a, b) => a.barcode.localeCompare(b.barcode, 'tr'));
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const list = e.target.files;
    if (!list || list.length === 0) return;
    const parsed = parseFolderSelection(list);
    // input'u sıfırla ki aynı klasör tekrar seçilebilsin
    e.target.value = '';
    if (parsed.length === 0) {
      setError(
        tt(
          'Seçilen klasörlerde resim bulunamadı. Her ürün için ayrı bir klasör (klasör adı = barkod) seçin.',
          'No images found in the selected folders. Use one folder per product (folder name = barcode).'
        )
      );
      return;
    }
    setFolders((prev) => mergeFolders(parsed, prev));
  };

  const handleRemoveFolder = (barcode: string) => {
    setFolders((prev) => prev.filter((f) => f.barcode !== barcode));
  };

  const handleClearFolders = () => {
    setFolders([]);
    setError(null);
  };

  const handleGoResolve = async () => {
    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
      return;
    }
    setResolving(true);
    setError(null);
    try {
      const barcodes = folders.map((f) => f.barcode);
      const results = await resolveByBarcodes(companyId, barcodes);
      const map = new Map<string, BarcodeResolveResult>();
      results.forEach((r) => map.set(r.barcode, r));
      setResolveMap(map);
      // Varsayılan: eşleşen tüm satırlar seçili
      const preSelected = new Set<string>();
      folders.forEach((f) => {
        const r = map.get(f.barcode);
        if (r?.found) preSelected.add(f.barcode);
      });
      setSelected(preSelected);
      setStep(1);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const apiMsg = err?.response?.data?.message || err?.response?.data?.title;
      if (status === 405 || status === 404) {
        setError(
          tt(
            'Barkod eşleştirme servisi şu an kullanılamıyor (API güncellemesi gerekli). Accounting API’yi yeniden başlatın veya deploy edin.',
            'Barcode matching service is unavailable (API update required). Restart or deploy the Accounting API.'
          )
        );
      } else {
        setError(
          apiMsg ||
            err?.message ||
            tt('Eşleştirme sırasında hata oluştu.', 'Error while matching.')
        );
      }
    } finally {
      setResolving(false);
    }
  };

  // ── Step 1: önizleme ──
  const foundCount = useMemo(
    () => folders.filter((f) => resolveMap.get(f.barcode)?.found).length,
    [folders, resolveMap]
  );

  const toggleRow = (barcode: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(barcode)) next.delete(barcode);
      else next.add(barcode);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === foundCount) return new Set();
      const next = new Set<string>();
      folders.forEach((f) => {
        if (resolveMap.get(f.barcode)?.found) next.add(f.barcode);
      });
      return next;
    });
  };

  // ── Step 2: yükleme ──
  const handleSubmit = async () => {
    if (!companyId) return;
    const selectedFolders = folders.filter(
      (f) => selected.has(f.barcode) && resolveMap.get(f.barcode)?.found
    );
    if (selectedFolders.length === 0) {
      setError(tt('Gönderilecek ürün seçilmedi.', 'No products selected.'));
      return;
    }
    const barcodeToItemId = new Map<string, string>();
    selectedFolders.forEach((f) => {
      const r = resolveMap.get(f.barcode);
      if (r?.itemId) barcodeToItemId.set(f.barcode, r.itemId);
    });

    setStep(2);
    setRunning(true);
    setError(null);
    try {
      const runResult = await runBulkAssign({
        companyId,
        createdBy,
        folders: selectedFolders,
        barcodeToItemId,
        maxImagesPerProduct,
        onProgress: setProgress,
      });
      setResult(runResult);
      onCompleted?.();
    } catch (err: any) {
      setError(err?.message || tt('Yükleme sırasında hata oluştu.', 'Error during upload.'));
    } finally {
      setRunning(false);
    }
  };

  const phaseLabel = (p?: AssignProgress | null) => {
    if (!p) return '';
    switch (p.phase) {
      case 'uploading':
        return tt('Resimler yükleniyor', 'Uploading images');
      case 'library':
        return tt('Kütüphaneye kaydediliyor', 'Saving to library');
      case 'assigning':
        return tt('Ürünlere atanıyor', 'Assigning to products');
      case 'done':
        return tt('Tamamlandı', 'Completed');
      default:
        return '';
    }
  };

  const canClose = !running && !resolving;

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveFolderUploadIcon color="primary" />
          <Typography variant="h6" component="span">
            {tt('Toplu Ürün Resmi Yükle', 'Bulk Product Image Upload')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={!canClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>{tt('Klasör Seç', 'Select Folders')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{tt('Önizleme', 'Preview')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{tt('Yükle', 'Upload')}</StepLabel>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 0 */}
        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {tt(
                'Diskte barkod adlı alt klasörleri olan ÜST klasörü seçin (ör. urun-resimleri → 8055…/, 8690…/). Tarayıcı aynı anda birden fazla klasör seçtirmese de üst klasörü seçince hepsi birden gelir. Tek barkod klasörü seçerseniz sadece o eklenir; tekrar tıklayarak başka klasör de ekleyebilirsiniz.',
                'Select the PARENT folder that contains barcode-named subfolders (e.g. product-images → 8055…/, 8690…/). Browsers only allow one folder pick, but choosing the parent imports all of them. Picking a single barcode folder adds only that one; click again to add more.'
              )}
            </Alert>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              // @ts-expect-error - non-standard attributes for directory upload
              webkitdirectory=""
              directory=""
              onChange={handleFilesSelected}
            />

            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={handlePickClick}
            >
              <DriveFolderUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {folders.length > 0
                  ? tt('Başka klasör eklemek için tıklayın', 'Click to add another folder')
                  : tt('Üst klasörü seçmek için tıklayın', 'Click to select the parent folder')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt(
                  'Önemli: İçinde birden fazla barkod klasörü olan üst klasörü seçin',
                  'Important: Select the parent folder that contains multiple barcode folders'
                )}
              </Typography>
            </Box>

            {folders.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {folders.length === 1 && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {tt(
                      'Sadece 1 klasör geldi. Toplu almak için tek barkod klasörünü değil, tüm barkod klasörlerini içeren üst klasörü seçin.',
                      'Only 1 folder was found. For bulk import, select the parent folder that contains all barcode folders — not a single barcode folder.'
                    )}
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {tt(
                      `${folders.length} klasör, ${totalImages} resim seçildi`,
                      `${folders.length} folders, ${totalImages} images selected`
                    )}
                  </Typography>
                  <Button size="small" color="inherit" onClick={handleClearFolders}>
                    {tt('Temizle', 'Clear')}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxHeight: 180,
                    overflowY: 'auto',
                    p: 0.5,
                  }}
                >
                  {folders.map((f) => (
                    <Chip
                      key={f.barcode}
                      label={`${f.barcode} (${f.files.length})`}
                      size="small"
                      variant="outlined"
                      onDelete={() => handleRemoveFolder(f.barcode)}
                      sx={{ fontFamily: 'monospace' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                color="success"
                variant="outlined"
                label={tt(`Eşleşen: ${foundCount}`, `Matched: ${foundCount}`)}
              />
              <Chip
                color="error"
                variant="outlined"
                label={tt(
                  `Eşleşmeyen: ${folders.length - foundCount}`,
                  `Unmatched: ${folders.length - foundCount}`
                )}
              />
              <Chip
                color="primary"
                variant="outlined"
                label={tt(`Seçili: ${selected.size}`, `Selected: ${selected.size}`)}
              />
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={foundCount > 0 && selected.size === foundCount}
                      indeterminate={selected.size > 0 && selected.size < foundCount}
                      onChange={toggleAll}
                      disabled={foundCount === 0}
                    />
                  </TableCell>
                  <TableCell>{tt('Resim', 'Image')}</TableCell>
                  <TableCell>{tt('Barkod', 'Barcode')}</TableCell>
                  <TableCell>{tt('Ürün', 'Product')}</TableCell>
                  <TableCell align="center">{tt('Mevcut', 'Existing')}</TableCell>
                  <TableCell align="center">{tt('Eklenecek', 'To add')}</TableCell>
                  <TableCell>{tt('Durum', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {folders.map((f) => {
                  const r = resolveMap.get(f.barcode);
                  const found = !!r?.found;
                  const src = previews.get(f.barcode);
                  return (
                    <TableRow key={f.barcode} hover selected={selected.has(f.barcode)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(f.barcode)}
                          onChange={() => toggleRow(f.barcode)}
                          disabled={!found}
                        />
                      </TableCell>
                      <TableCell>
                        {src ? (
                          <Box
                            component="img"
                            src={src}
                            alt={f.barcode}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {f.barcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {found ? (
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {r?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r?.stockCode}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{r?.imageUrls?.length ?? 0}</TableCell>
                      <TableCell align="center">{f.files.length}</TableCell>
                      <TableCell>
                        {found ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            color="success"
                            size="small"
                            variant="outlined"
                            label={tt('Eşleşti', 'Matched')}
                          />
                        ) : (
                          <Tooltip
                            title={
                              (r?.duplicateCount ?? 0) > 1
                                ? tt(
                                    'Bu barkoda sahip birden fazla ürün var',
                                    'Multiple products share this barcode'
                                  )
                                : tt('Ürün bulunamadı', 'Product not found')
                            }
                          >
                            <Chip
                              icon={<ErrorOutlineIcon />}
                              color="error"
                              size="small"
                              variant="outlined"
                              label={
                                (r?.duplicateCount ?? 0) > 1
                                  ? tt('Mükerrer', 'Duplicate')
                                  : tt('Bulunamadı', 'Not found')
                              }
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Box>
            {running && (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    {phaseLabel(progress)}
                    {progress?.currentBarcode ? ` · ${progress.currentBarcode}` : ''}
                  </Typography>
                </Box>
                <LinearProgress
                  variant={progress && progress.total > 0 ? 'determinate' : 'indeterminate'}
                  value={
                    progress && progress.total > 0
                      ? (progress.current / progress.total) * 100
                      : undefined
                  }
                />
                {progress && progress.total > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {progress.current} / {progress.total}
                  </Typography>
                )}
              </Box>
            )}

            {result && !running && (
              <Box>
                <Alert
                  severity={result.failedFolders === 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  {tt(
                    `${result.successFolders} ürün güncellendi, ${result.totalAdded} resim eklendi.`,
                    `${result.successFolders} products updated, ${result.totalAdded} images added.`
                  )}
                  {result.failedFolders > 0 &&
                    tt(
                      ` ${result.failedFolders} klasör başarısız.`,
                      ` ${result.failedFolders} folders failed.`
                    )}
                </Alert>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{tt('Barkod', 'Barcode')}</TableCell>
                      <TableCell align="center">{tt('Yüklendi', 'Uploaded')}</TableCell>
                      <TableCell align="center">{tt('Eklendi', 'Added')}</TableCell>
                      <TableCell align="center">{tt('Atlandı', 'Skipped')}</TableCell>
                      <TableCell>{tt('Durum', 'Status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.outcomes.map((o) => (
                      <TableRow key={o.barcode}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{o.barcode}</TableCell>
                        <TableCell align="center">{o.uploadedCount}</TableCell>
                        <TableCell align="center">{o.addedCount}</TableCell>
                        <TableCell align="center">{o.skippedCount}</TableCell>
                        <TableCell>
                          {o.success ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              color="success"
                              size="small"
                              variant="outlined"
                              label={tt('Başarılı', 'Success')}
                            />
                          ) : (
                            <Tooltip title={o.error || ''}>
                              <Chip
                                icon={<ErrorOutlineIcon />}
                                color="error"
                                size="small"
                                variant="outlined"
                                label={tt('Hata', 'Error')}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={onClose} color="inherit">
              {tt('İptal', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleGoResolve}
              disabled={folders.length === 0 || resolving || !companyId}
              startIcon={resolving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {tt('Devam', 'Continue')}
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} color="inherit">
              {tt('Geri', 'Back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={selected.size === 0}
            >
              {tt(`Gönder (${selected.size})`, `Submit (${selected.size})`)}
            </Button>
          </>
        )}

        {step === 2 && (
          <Button variant="contained" onClick={onClose} disabled={running}>
            {tt('Kapat', 'Close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkProductImageAssignWizard;
